const AUTH_URL = import.meta.env.VITE_AUTH_API_URL || 'https://hannah-auth.erikabarker7.workers.dev';
const TOKEN_KEY = 'hannah_session_token';
const EXPIRES_KEY = 'hannah_session_expires';
const ENTITY_KEY = 'hannah_entity_id';
const DISPLAY_NAME_KEY = 'hannah_display_name';

interface AuthResponse {
  success: boolean;
  token?: string;
  expiresAt?: number;
  entityId?: string;
  displayName?: string;
  error?: string;
}

interface VerifyResponse {
  valid: boolean;
  username?: string;
  expiresAt?: number;
}

export async function login(
  username: string, 
  password: string, 
  turnstileToken?: string
): Promise<boolean> {
  try {
    const response = await fetch(`${AUTH_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, turnstileToken })
    });

    const data: AuthResponse = await response.json();

    if (data.success && data.token) {
      // Clear any previous user's chat history before switching entities
      const prevEntity = localStorage.getItem(ENTITY_KEY);
      if (prevEntity && data.entityId && prevEntity !== data.entityId) {
        localStorage.removeItem(`hannah_chat_history_${prevEntity}`);
        localStorage.removeItem('hannah_chat_history'); // legacy key
      }

      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(EXPIRES_KEY, String(data.expiresAt));
      if (data.entityId) {
        localStorage.setItem(ENTITY_KEY, data.entityId);
      }
      if (data.displayName) {
        localStorage.setItem(DISPLAY_NAME_KEY, data.displayName);
      }
      return true;
    }

    return false;
  } catch (error) {
    console.error('Login error:', error);
    return false;
  }
}

export async function logout(): Promise<void> {
  const token = localStorage.getItem(TOKEN_KEY);
  // Clear per-entity chat history before removing entity key
  const entityId = localStorage.getItem(ENTITY_KEY);
  if (entityId) {
    localStorage.removeItem(`hannah_chat_history_${entityId}`);
  }
  localStorage.removeItem('hannah_chat_history'); // legacy key cleanup
  if (token) {
    try {
      await fetch(`${AUTH_URL}/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  }
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EXPIRES_KEY);
  localStorage.removeItem(ENTITY_KEY);
  localStorage.removeItem(DISPLAY_NAME_KEY);
}

export async function verifySession(): Promise<boolean> {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return false;

  try {
    const response = await fetch(`${AUTH_URL}/auth/verify`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    const data: VerifyResponse = await response.json();
    if (response.ok && data.valid === true) {
      if (data.expiresAt) localStorage.setItem(EXPIRES_KEY, String(data.expiresAt));
      return true;
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRES_KEY);
    return false;
  } catch (error) {
    console.error('Session verify error:', error);
    return false;
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function isSessionValid(): boolean {
  const token = localStorage.getItem(TOKEN_KEY);
  const expires = localStorage.getItem(EXPIRES_KEY);

  // The server is authoritative. The cached timestamp can be stale because every verified creator chat
  // now slides the server-side expiry; useAuth immediately follows this check with verifySession().
  return !!token && !!expires;
}

export function getEntityId(): string {
  return localStorage.getItem(ENTITY_KEY) || 'unknown';
}

export function getDisplayName(): string {
  return localStorage.getItem(DISPLAY_NAME_KEY) || 'User';
}

/** True only when the logged-in user is the creator (Erika). */
export function isCreator(): boolean {
  return getEntityId() === 'erika';
}
